import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ListingCard from '../components/ListingCard';
import { Badge } from '../components/UI';
import { useAuth } from '../context/AuthContext';

const CATEGORIES = ['All', 'cellphones', 'tablets', 'laptops', 'servers', 'hard_drives', 'ddr_ram', 'other'];
const CATEGORY_LABELS = {
  All: 'All', cellphones: 'Cellphones', tablets: 'Tablets', laptops: 'Laptops',
  servers: 'Servers', hard_drives: 'Hard Drives', ddr_ram: 'DDR RAM', other: 'Other',
};

export default function BrowsePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [listings, setListings] = useState([]);
  const [endedListings, setEndedListings] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('All');
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('newest');

  useEffect(() => {
    setLoading(true);
    const params = { sort };
    if (category !== 'All') params.category = category;
    if (search) params.search = search;

    const fetches = [
      api.getListings({ ...params, status: 'ended,awarded,completed,paid,shipped' }),
    ];

    // Only fetch active listings if logged in
    if (user) {
      fetches.unshift(api.getListings(params));
    }

    Promise.all(fetches)
      .then((results) => {
        if (user) {
          setListings(results[0].items || []);
          setTotal(results[0].total || 0);
          setEndedListings(results[1].items || []);
        } else {
          setListings([]);
          setTotal(0);
          setEndedListings(results[0].items || []);
        }
      })
      .catch(err => console.error('Failed to load listings:', err))
      .finally(() => setLoading(false));
  }, [category, search, sort, user]);

  return (
    <div style={{ padding: 32 }}>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, var(--bg2) 0%, var(--surface) 100%)',
        border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '48px 40px',
        marginBottom: 32, position: 'relative', overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -80, right: -80, width: 300, height: 300,
          borderRadius: '50%', background: 'var(--accent-dim)', filter: 'blur(100px)', opacity: 0.4,
        }} />
        <div style={{ position: 'relative', zIndex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
            <Badge color="green">
              <span className="pulse-dot" style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--accent)', display: 'inline-block' }} />
              {total} LISTINGS
            </Badge>
          </div>
          <h1 style={{ fontSize: 40, fontWeight: 900, color: 'var(--text)', fontFamily: 'var(--sans)', lineHeight: 1.1, marginBottom: 10, letterSpacing: '-0.03em' }}>
            R2-Certified<br />Electronics Marketplace
          </h1>
          <p style={{ fontSize: 15, color: 'var(--text3)', fontFamily: 'var(--sans)', maxWidth: 500, lineHeight: 1.6 }}>
            Verified recycled electronics. Transparent bidding. Trusted by R2-certified buyers and sellers worldwide.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24, flexWrap: 'wrap', gap: 16 }}>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {CATEGORIES.map(c => (
            <button key={c} onClick={() => setCategory(c)} style={{
              padding: '8px 16px', borderRadius: 8, border: '1px solid',
              borderColor: category === c ? 'var(--accent)' : 'var(--border)',
              background: category === c ? 'var(--accent-dim)' : 'transparent',
              color: category === c ? 'var(--accent)' : 'var(--text3)',
              fontFamily: 'var(--sans)', fontSize: 13, fontWeight: 500, cursor: 'pointer', transition: 'all 0.2s',
            }}>{CATEGORY_LABELS[c]}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <select value={sort} onChange={e => setSort(e.target.value)} style={{
            padding: '10px 14px', background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', color: 'var(--text)', fontSize: 13, fontFamily: 'var(--sans)', outline: 'none',
          }}>
            <option value="newest">Newest</option>
            <option value="ending_soon">Ending Soon</option>
            <option value="price_asc">Price: Low → High</option>
            <option value="price_desc">Price: High → Low</option>
            <option value="most_bids">Most Bids</option>
          </select>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, background: 'var(--surface)', border: '1px solid var(--border)',
            borderRadius: 'var(--radius)', padding: '0 14px', minWidth: 220,
          }}>
            <span style={{ color: 'var(--text3)', fontSize: 14 }}>🔍</span>
            <input
              value={search} onChange={e => setSearch(e.target.value)} placeholder="Search listings..."
              style={{ background: 'none', border: 'none', outline: 'none', color: 'var(--text)', padding: '10px 0', fontSize: 13, fontFamily: 'var(--sans)', width: '100%' }}
            />
          </div>
        </div>
      </div>

      {/* Active Listings — logged in users only */}
      {!user ? (
        <div style={{
          margin: '0 0 48px', padding: '48px 40px', borderRadius: 'var(--radius-lg)',
          border: '1px dashed rgba(34,214,122,0.3)',
          background: 'radial-gradient(ellipse 80% 100% at 50% 0%, rgba(34,214,122,0.06) 0%, transparent 70%)',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 16 }}>🔒</div>
          <h3 style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.02em', margin: '0 0 10px', color: 'var(--text)' }}>
            Log in to see active auctions
          </h3>
          <p style={{ fontSize: 15, color: 'var(--text3)', margin: '0 0 28px', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6 }}>
            Active listings with live bidding are only visible to registered members. Past auctions are shown below so you can see the kind of inventory available.
          </p>
          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
            <button onClick={() => navigate('/login')} style={{
              padding: '12px 32px', borderRadius: 10, border: 'none', cursor: 'pointer',
              background: 'var(--accent)', color: '#000', fontSize: 15, fontWeight: 700,
              fontFamily: 'var(--sans)', transition: 'all 0.2s',
            }}>Log In</button>
            <button onClick={() => navigate('/register')} style={{
              padding: '12px 32px', borderRadius: 10, cursor: 'pointer', fontSize: 15, fontWeight: 600,
              background: 'transparent', border: '1px solid var(--border)', color: 'var(--text2)',
              fontFamily: 'var(--sans)', transition: 'all 0.2s',
            }}>Register</button>
          </div>
        </div>
      ) : loading ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)' }}>Loading listings...</div>
      ) : listings.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: 'var(--text3)', fontFamily: 'var(--sans)' }}>
          No active listings yet. Check back soon!
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 20 }}>
          {listings.map((item, i) => (
            <div key={item.id} className="fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <ListingCard listing={item} />
            </div>
          ))}
        </div>
      )}

      {/* Past Auctions Section */}
      {!loading && endedListings.length > 0 && (
        <div style={{ marginTop: 56 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 24 }}>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text3)', fontFamily: 'var(--mono)', letterSpacing: '0.08em' }}>
              🏁 PAST AUCTIONS
            </span>
            <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(290px, 1fr))', gap: 20 }}>
            {endedListings.map((item, i) => (
              <div key={item.id} className="fade-up" style={{ animationDelay: `${i * 60}ms` }}>
                <ListingCard listing={item} hidePricing />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
